import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import adminApi, {
  AiModelOption,
  AiModelRoute,
  AiModelsOverview,
  AiProviderStatus,
  AiTelemetryRow,
  TestAiModelResult,
} from '../../api/admin';
import { useLanguage } from '../../contexts/LanguageContext';

const FEATURE_LABELS: Record<string, string> = {
  OCR: 'OCR (lecture de factures)',
  RELANCE_EMAIL: 'Relances par email',
  DASHBOARD_ASSISTANT: 'Assistant tableau de bord',
};

interface FormState {
  provider: string;
  model: string;
  temperature: number | null;
  maxTokens: number | null;
  jsonMode: boolean;
}

const sig = (f: FormState) =>
  JSON.stringify([f.provider, f.model.trim(), f.temperature, f.maxTokens, f.jsonMode]);

const AiModels: React.FC = () => {
  const { t } = useLanguage();
  const tr = t.admin?.aiModels;
  const [overview, setOverview] = useState<AiModelsOverview | null>(null);
  const [loading, setLoading] = useState(false);

  // Per-provider model catalog, fetched lazily the first time a picker is opened.
  const [modelsCache, setModelsCache] = useState<Record<string, AiModelOption[]>>({});
  const [modelsLoading, setModelsLoading] = useState<Record<string, boolean>>({});
  const [modelsError, setModelsError] = useState<Record<string, string | null>>({});
  const requested = useRef<Set<string>>(new Set());

  const ensureModels = useCallback((provider: string) => {
    if (!provider || requested.current.has(provider)) return;
    requested.current.add(provider);
    setModelsLoading((s) => ({ ...s, [provider]: true }));
    setModelsError((s) => ({ ...s, [provider]: null }));
    adminApi
      .getAvailableModels(provider)
      .then((list) => setModelsCache((c) => ({ ...c, [provider]: list })))
      .catch((e) => {
        requested.current.delete(provider); // allow retry after a failure
        setModelsError((s) => ({ ...s, [provider]: e?.response?.data?.message || 'Erreur' }));
      })
      .finally(() => setModelsLoading((s) => ({ ...s, [provider]: false })));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    // Drop cached catalogs so a refresh re-fetches (e.g. after configuring a provider).
    requested.current.clear();
    setModelsCache({});
    setModelsError({});
    adminApi
      .getAiModels()
      .then(setOverview)
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{tr?.title ?? 'Modèles IA'}</h1>
          <p className="text-sm text-slate-500">
            {tr?.subtitle ??
              'Modifiez le fournisseur et le modèle utilisés par chaque fonctionnalité IA. Testez avant d’enregistrer.'}
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {tr?.refresh ?? 'Rafraîchir'}
        </button>
      </div>

      {overview && !overview.enabled && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            {tr?.disabledWarning ??
              'L’IA est désactivée globalement (app.llm.enabled=false). Les modifications sont enregistrées mais non utilisées tant que l’IA n’est pas réactivée.'}
          </span>
        </div>
      )}

      {overview && (
        <div className="flex flex-wrap gap-2">
          {overview.providers.map((p) => (
            <span
              key={p.name}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                p.available ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${p.available ? 'bg-green-500' : 'bg-slate-400'}`}
              />
              {p.name}
              {!p.available && ` · ${tr?.notConfigured ?? 'non configuré'}`}
            </span>
          ))}
        </div>
      )}

      {!overview && loading && <div className="text-slate-500">{t.common.loading}</div>}

      <AiTelemetryCard />

      <div className="space-y-4">
        {overview?.routes.map((route) => (
          <RouteEditor
            key={route.feature}
            route={route}
            providers={overview.providers}
            onSaved={load}
            modelsCache={modelsCache}
            modelsLoading={modelsLoading}
            modelsError={modelsError}
            ensureModels={ensureModels}
          />
        ))}
      </div>
    </div>
  );
};

interface RouteEditorProps {
  route: AiModelRoute;
  providers: AiProviderStatus[];
  onSaved: () => void;
  modelsCache: Record<string, AiModelOption[]>;
  modelsLoading: Record<string, boolean>;
  modelsError: Record<string, string | null>;
  ensureModels: (provider: string) => void;
}

const RouteEditor: React.FC<RouteEditorProps> = ({
  route,
  providers,
  onSaved,
  modelsCache,
  modelsLoading,
  modelsError,
  ensureModels,
}) => {
  const { t } = useLanguage();
  const tr = t.admin?.aiModels;

  const initial: FormState = useMemo(
    () => ({
      provider: route.provider,
      model: route.model,
      temperature: route.temperature,
      maxTokens: route.maxTokens,
      jsonMode: route.jsonMode,
    }),
    [route]
  );

  const [form, setForm] = useState<FormState>(initial);
  const [savedSig, setSavedSig] = useState(sig(initial));
  const [testedSig, setTestedSig] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestAiModelResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentSig = sig(form);
  const dirty = currentSig !== savedSig;
  const testPassedForCurrent = testResult?.success === true && testedSig === currentSig;
  const canSave = dirty && testPassedForCurrent;

  const update = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const runTest = () => {
    if (!form.model.trim()) {
      toast.error(tr?.modelRequired ?? 'Le modèle est requis.');
      return;
    }
    setTesting(true);
    setTestResult(null);
    adminApi
      .testAiModel({
        provider: form.provider,
        model: form.model.trim(),
        temperature: form.temperature,
        maxTokens: form.maxTokens,
        jsonMode: form.jsonMode,
      })
      .then((res) => {
        setTestResult(res);
        if (res.success) {
          setTestedSig(sig(form));
          toast.success(tr?.testOk ?? 'Test réussi');
        } else {
          setTestedSig(null);
        }
      })
      .catch((e) => {
        setTestedSig(null);
        toast.error(e?.response?.data?.message || (tr?.testFailed ?? 'Échec du test'));
      })
      .finally(() => setTesting(false));
  };

  const save = () => {
    setSaving(true);
    adminApi
      .updateAiModel(route.feature, {
        provider: form.provider,
        model: form.model.trim(),
        temperature: form.temperature,
        maxTokens: form.maxTokens,
        jsonMode: form.jsonMode,
      })
      .then(() => {
        setSavedSig(sig(form));
        toast.success(tr?.saved ?? 'Modèle enregistré');
        onSaved();
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setSaving(false));
  };

  const featureLabel =
    (tr?.features as Record<string, string> | undefined)?.[route.feature] ??
    FEATURE_LABELS[route.feature] ??
    route.feature;
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-900">{featureLabel}</h3>
          <p className="font-mono text-xs text-slate-400">{route.feature}</p>
        </div>
        {dirty && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            {tr?.unsaved ?? 'Non enregistré'}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-500">{tr?.provider ?? 'Fournisseur'}</span>
          <select
            value={form.provider}
            onChange={(e) => update({ provider: e.target.value })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {providers.map((p) => (
              <option key={p.name} value={p.name} disabled={!p.available}>
                {p.name}
                {!p.available ? ` (${tr?.notConfigured ?? 'non configuré'})` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm sm:col-span-2 lg:col-span-3">
          <span className="text-slate-500">{tr?.model ?? 'Modèle'}</span>
          <ModelPicker
            value={form.model}
            onChange={(model) => update({ model })}
            models={modelsCache[form.provider]}
            loading={!!modelsLoading[form.provider]}
            error={modelsError[form.provider] ?? null}
            onOpen={() => ensureModels(form.provider)}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-500">{tr?.temperature ?? 'Température'}</span>
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={form.temperature ?? ''}
            onChange={(e) => update({ temperature: numOrNull(e.target.value) })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-500">{tr?.maxTokens ?? 'Tokens max'}</span>
          <input
            type="number"
            min="1"
            value={form.maxTokens ?? ''}
            onChange={(e) => update({ maxTokens: numOrNull(e.target.value) })}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </label>

        <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={form.jsonMode}
            onChange={(e) => update({ jsonMode: e.target.checked })}
          />
          {tr?.jsonMode ?? 'Mode JSON'}
        </label>
      </div>

      {testResult && <TestResultPanel result={testResult} />}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={runTest}
          disabled={testing}
          className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50"
        >
          <BeakerIcon className={`h-4 w-4 ${testing ? 'animate-pulse' : ''}`} />
          {testing ? tr?.testing ?? 'Test en cours…' : tr?.test ?? 'Tester'}
        </button>
        <button
          onClick={save}
          disabled={!canSave || saving}
          title={!canSave && dirty ? tr?.testFirst ?? 'Testez avec succès avant d’enregistrer' : undefined}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? tr?.saving ?? 'Enregistrement…' : tr?.save ?? 'Enregistrer'}
        </button>
        {dirty && !testPassedForCurrent && (
          <span className="text-xs text-slate-400">
            {tr?.testFirst ?? 'Testez avec succès avant d’enregistrer'}
          </span>
        )}
      </div>
    </div>
  );
};

const fmtContext = (n: number | null | undefined): string | null => {
  if (!n) return null;
  return n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`;
};

const fmtUsdPerMillion = (perToken: number | null | undefined): string | null => {
  if (perToken == null) return null;
  const perM = perToken * 1_000_000;
  if (perM === 0) return '$0';
  if (perM >= 1) return `$${perM.toFixed(2)}`;
  if (perM >= 0.01) return `$${perM.toFixed(3)}`;
  return `$${perM.toPrecision(2)}`;
};

const isFreeModel = (m: AiModelOption): boolean =>
  (m.promptPrice ?? 0) === 0 && (m.completionPrice ?? 0) === 0;

const isVisionModel = (m: AiModelOption): boolean =>
  !!m.modalities?.some((mod) => mod.toLowerCase() === 'image');

interface ModelPickerProps {
  value: string;
  onChange: (model: string) => void;
  models: AiModelOption[] | undefined;
  loading: boolean;
  error: string | null;
  onOpen: () => void;
}

const ModelPicker: React.FC<ModelPickerProps> = ({
  value,
  onChange,
  models,
  loading,
  error,
  onOpen,
}) => {
  const { t } = useLanguage();
  const tr = t.admin?.aiModels;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = models?.find((m) => m.id === value);

  const filtered = useMemo(() => {
    const list = models ?? [];
    const v = value.trim().toLowerCase();
    if (!v) return list;
    // When the value already matches a model exactly, show the whole list so the
    // operator can keep browsing; otherwise treat the field as a search query.
    if (list.some((m) => m.id.toLowerCase() === v)) return list;
    return list.filter(
      (m) => m.id.toLowerCase().includes(v) || m.name.toLowerCase().includes(v)
    );
  }, [models, value]);

  const openDropdown = () => {
    onOpen();
    setOpen(true);
  };

  const priceLabel = (m: AiModelOption): string | null => {
    if (m.promptPrice == null && m.completionPrice == null) return null;
    if (isFreeModel(m)) return tr?.free ?? 'Gratuit';
    const inP = fmtUsdPerMillion(m.promptPrice) ?? '—';
    const outP = fmtUsdPerMillion(m.completionPrice) ?? '—';
    return `${inP} / ${outP}`;
  };

  const MAX_ROWS = 80;
  const shown = filtered.slice(0, MAX_ROWS);

  return (
    <div className="relative" ref={containerRef}>
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={openDropdown}
        placeholder="qwen/qwen3-next-80b-a3b-instruct:free"
        className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm"
        autoComplete="off"
        spellCheck={false}
      />

      {selected && !open && (
        <p className="mt-1 truncate text-xs text-slate-400">
          {[
            fmtContext(selected.contextLength) &&
              `${fmtContext(selected.contextLength)} ${tr?.tokens ?? 'tokens'}`,
            priceLabel(selected) &&
              `${tr?.priceLabel ?? 'Prix (in/out par 1M tokens)'}: ${priceLabel(selected)}`,
            isVisionModel(selected) && (tr?.vision ?? 'vision'),
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}

      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-sm text-slate-400">
              {tr?.loadingModels ?? 'Chargement des modèles…'}
            </div>
          )}
          {!loading && error && (
            <div className="px-3 py-2 text-sm text-red-500">{error}</div>
          )}
          {!loading && !error && shown.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">
              {(models ?? []).length === 0
                ? tr?.noModels ?? 'Aucun modèle disponible. Saisissez un identifiant manuellement.'
                : tr?.noMatch ?? 'Aucun modèle ne correspond.'}
            </div>
          )}
          {!loading &&
            !error &&
            shown.map((m) => {
              const ctx = fmtContext(m.contextLength);
              const price = priceLabel(m);
              const free = isFreeModel(m);
              return (
                <button
                  type="button"
                  key={m.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(m.id);
                    setOpen(false);
                  }}
                  className={`flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 ${
                    m.id === value ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800">{m.name}</div>
                    <div className="truncate font-mono text-xs text-slate-400">{m.id}</div>
                  </div>
                  <div className="flex flex-shrink-0 flex-col items-end gap-0.5 text-xs">
                    {ctx && <span className="text-slate-500">{ctx} ctx</span>}
                    {price && (
                      <span className={free ? 'font-medium text-green-600' : 'text-slate-500'}>
                        {price}
                      </span>
                    )}
                    {isVisionModel(m) && (
                      <span className="rounded bg-indigo-50 px-1.5 text-indigo-600">
                        {tr?.vision ?? 'vision'}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          {!loading && !error && filtered.length > MAX_ROWS && (
            <div className="px-3 py-2 text-xs text-slate-400">
              {tr?.moreModels ?? 'Affinez votre recherche pour voir plus de modèles…'}
            </div>
          )}
          {!loading && !error && (models ?? []).length > 0 && (
            <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-400">
              {tr?.priceHint ?? 'Prix indiqués en USD par 1M tokens (entrée / sortie).'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TestResultPanel: React.FC<{ result: TestAiModelResult }> = ({ result }) => {
  const { t } = useLanguage();
  const tr = t.admin?.aiModels;

  return (
    <div
      className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
        result.success
          ? 'border-green-200 bg-green-50 text-green-900'
          : 'border-red-200 bg-red-50 text-red-900'
      }`}
    >
      <div className="flex items-center gap-2 font-medium">
        {result.success ? (
          <CheckCircleIcon className="h-5 w-5 text-green-600" />
        ) : (
          <XCircleIcon className="h-5 w-5 text-red-600" />
        )}
        {result.success
          ? `${tr?.testOk ?? 'Test réussi'} · ${result.latencyMs} ms · ${result.tokensIn}/${result.tokensOut} tokens`
          : `${tr?.testFailed ?? 'Échec du test'}${result.statusCode ? ` · HTTP ${result.statusCode}` : ''}`}
      </div>
      {result.success && result.content && (
        <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-white/60 p-2 font-mono text-xs">
          {result.content}
        </pre>
      )}
      {!result.success && result.error && (
        <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-white/60 p-2 font-mono text-xs">
          {result.error}
        </pre>
      )}
    </div>
  );
};

/** TF-066 — observabilité de la couche garde-fous IA (appels / replis déterministes). */
const AiTelemetryCard: React.FC = () => {
  const [rows, setRows] = useState<AiTelemetryRow[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    adminApi.getAiTelemetry().then(setRows).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-700 mb-1">Garde-fous IA — télémétrie</h2>
      <p className="text-xs text-slate-400 mb-3">
        Chaque fonctionnalité IA s’exécute sous garde-fous (tenant + repli déterministe). Taux de
        repli élevé = LLM indisponible, mais la fonctionnalité continue de marcher.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune invocation IA depuis le dernier démarrage.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="text-xs text-slate-400 uppercase">
            <tr className="text-left">
              <th className="py-1 pr-4">Fonctionnalité</th>
              <th className="py-1 pr-4 text-right">Appels</th>
              <th className="py-1 pr-4 text-right">Replis</th>
              <th className="py-1 text-right">Taux de repli</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.feature}>
                <td className="py-1.5 pr-4 font-medium text-slate-700">{r.feature}</td>
                <td className="py-1.5 pr-4 text-right">{r.calls}</td>
                <td className="py-1.5 pr-4 text-right">{r.fallbacks}</td>
                <td className={`py-1.5 text-right font-semibold ${
                  r.fallbackRatePct >= 50 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {r.fallbackRatePct}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AiModels;
